import { useState, useEffect, useCallback } from "react"
import { Container, Row, Col, Spinner, Card, Table, Badge, Form, Button, InputGroup } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "../../common/API"
import DateRangeFilter from "../../components/indicators/DateRangeFilter"
import IndicatorCard from "../../components/indicators/IndicatorCard"
import Pagination from "../../components/core/Pagination"
import OrderTracePanel from "../../components/Documents/OrderTracePanel"
import moment from "moment"
import { MerchandiseSearchResult, MerchandiseRecentDispatch, DispatchDetailClient } from "../../types/Indicators.types"

interface Props {
  orgId: string
}

interface FilterOptions {
  warehouses: string[]
  dependencies: string[]
}

const PAGE_SIZE = 20

export default function MerchandiseSearch({ orgId }: Props) {
  const { t } = useTranslation("global")

  const [term, setTerm] = useState("")
  const [searchedTerm, setSearchedTerm] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<MerchandiseSearchResult | null>(null)
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"))
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"))

  const [filterOptions, setFilterOptions] = useState<FilterOptions>({ warehouses: [], dependencies: [] })
  const [dependency, setDependency] = useState("")
  const [warehouse, setWarehouse] = useState("")

  const [recent, setRecent] = useState<MerchandiseRecentDispatch[]>([])
  const [isLoadingRecent, setIsLoadingRecent] = useState(false)
  const [topClients, setTopClients] = useState<DispatchDetailClient[]>([])
  const [isLoadingTopClients, setIsLoadingTopClients] = useState(false)
  const [selectedItemCode, setSelectedItemCode] = useState("")
  const [traceOrder, setTraceOrder] = useState("")
  const [page, setPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)

  const loadFilterOptions = useCallback(async () => {
    if (!orgId) return
    try {
      const res = await API.getAction("Indicators/MerchandiseFilterOptions", `organizationId=${orgId}`)
      if (res.data.status === "ok") {
        setFilterOptions({
          warehouses: res.data.response?.warehouses ?? [],
          dependencies: res.data.response?.dependencies ?? [],
        })
      }
    } catch (ex) { console.error(ex) }
  }, [orgId])

  useEffect(() => {
    setResult(null)
    setRecent([])
    setSearchedTerm("")
    setSelectedItemCode("")
    setDependency("")
    setWarehouse("")
    loadFilterOptions()
  }, [orgId, loadFilterOptions])

  const buildFilterParams = (dep: string, wh: string) => {
    let params = ""
    if (dep) params += `&dependency=${encodeURIComponent(dep)}`
    if (wh) params += `&warehouse=${encodeURIComponent(wh)}`
    return params
  }

  const loadRecent = async (searchTerm: string, itemCode: string, newPage: number, start: string, end: string, dep: string, wh: string) => {
    if (!orgId) return
    try {
      setIsLoadingRecent(true)
      let params = `organizationId=${orgId}&startDate=${start}&endDate=${end}&page=${newPage}&quantityPerPage=${PAGE_SIZE}`
      params += itemCode
        ? `&itemCode=${encodeURIComponent(itemCode)}`
        : `&term=${encodeURIComponent(searchTerm)}`
      params += buildFilterParams(dep, wh)
      const res = await API.getAction("Indicators/MerchandiseRecentDispatches", params)
      if (res.data.status === "ok") {
        setRecent(res.data.response ?? [])
        setTotalPages(res.data.quantityOfPages ?? 0)
        setTotalRecords(res.data.quantityOfRecords ?? 0)
        setPage(newPage)
      }
    } catch (ex) { console.error(ex) }
    finally { setIsLoadingRecent(false) }
  }

  const loadTopClients = async (searchTerm: string, itemCode: string, start: string, end: string, dep: string, wh: string) => {
    if (!orgId) return
    try {
      setIsLoadingTopClients(true)
      let params = `organizationId=${orgId}&startDate=${start}&endDate=${end}`
      params += itemCode
        ? `&itemCode=${encodeURIComponent(itemCode)}`
        : `&term=${encodeURIComponent(searchTerm)}`
      params += buildFilterParams(dep, wh)
      const res = await API.getAction("Indicators/MerchandiseTopClients", params)
      if (res.data.status === "ok") setTopClients(res.data.response ?? [])
    } catch (ex) { console.error(ex) }
    finally { setIsLoadingTopClients(false) }
  }

  const search = async (start = startDate, end = endDate, dep = dependency, wh = warehouse) => {
    if (!orgId || !term.trim()) return
    try {
      setIsLoading(true)
      setStartDate(start)
      setEndDate(end)
      setSearchedTerm(term.trim())
      setSelectedItemCode("")
      let params = `organizationId=${orgId}&startDate=${start}&endDate=${end}&term=${encodeURIComponent(term.trim())}`
      params += buildFilterParams(dep, wh)
      const res = await API.getAction("Indicators/MerchandiseSearch", params)
      if (res.data.status === "ok") setResult(res.data.response ?? null)
      await Promise.all([
        loadRecent(term.trim(), "", 0, start, end, dep, wh),
        loadTopClients(term.trim(), "", start, end, dep, wh),
      ])
    } catch (ex) { console.error(ex) }
    finally { setIsLoading(false) }
  }

  const changeDependency = (value: string) => {
    setDependency(value)
    if (searchedTerm) search(startDate, endDate, value, warehouse)
  }

  const changeWarehouse = (value: string) => {
    setWarehouse(value)
    if (searchedTerm) search(startDate, endDate, dependency, value)
  }

  const filterByProduct = (itemCode: string) => {
    const next = selectedItemCode === itemCode ? "" : itemCode
    setSelectedItemCode(next)
    loadRecent(searchedTerm, next, 0, startDate, endDate, dependency, warehouse)
    loadTopClients(searchedTerm, next, startDate, endDate, dependency, warehouse)
  }

  const monthLabel = (m: { year: number; month: number }) =>
    moment(`${m.year}-${String(m.month).padStart(2, "0")}-01`).format("MMM YYYY")

  return (
    <Container fluid className="px-0 py-3">
      <h5 className="mb-3">
        <i className="fas fa-magnifying-glass-chart me-2 text-primary" />
        {t("Concept Search")}
      </h5>

      <Card className="shadow-sm border-0 mb-4">
        <Card.Body>
          <DateRangeFilter onFilter={(s, e) => { if (searchedTerm) { search(s, e) } else { setStartDate(s); setEndDate(e) } }} loading={isLoading} />

          <Row className="g-2 align-items-end">
            <Col md={5} lg={4}>
              <Form.Label className="small text-muted mb-1">{t("Concept")}</Form.Label>
              <InputGroup size="sm">
                <InputGroup.Text><i className="fas fa-search" /></InputGroup.Text>
                <Form.Control
                  placeholder={t("Search concept, e.g. acero, varilla, blocks")}
                  value={term}
                  onChange={e => setTerm(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") search() }}
                />
                <Button variant="primary" onClick={() => search()} disabled={isLoading || !term.trim() || !orgId}>
                  {isLoading ? <i className="fas fa-spinner fa-spin" /> : t("Search")}
                </Button>
              </InputGroup>
            </Col>
            <Col md={3} lg={3}>
              <Form.Label className="small text-muted mb-1">{t("Dependency / Zone")}</Form.Label>
              <Form.Select size="sm" value={dependency} onChange={e => changeDependency(e.target.value)}>
                <option value="">{t("All dependencies")}</option>
                {filterOptions.dependencies.map(d => <option key={d} value={d}>{d}</option>)}
              </Form.Select>
            </Col>
            <Col md={3} lg={3}>
              <Form.Label className="small text-muted mb-1">{t("Origin Warehouse")}</Form.Label>
              <Form.Select size="sm" value={warehouse} onChange={e => changeWarehouse(e.target.value)}>
                <option value="">{t("All warehouses")}</option>
                {filterOptions.warehouses.map(w => <option key={w} value={w}>{w}</option>)}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!orgId ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-4 text-center text-muted">
            {t("Select an organization to load dispatch indicators")}
          </Card.Body>
        </Card>
      ) : !searchedTerm ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-4 text-center text-muted">
            {t("Type a concept to analyze merchandise movement")}
          </Card.Body>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : result && (
        <>
          <Row className="g-3 mb-4">
            <Col sm={6} lg={2}>
              <IndicatorCard title={t("Total Quantity Moved")} value={Math.round(result.summary.totalQuantity)} icon="fa-boxes-stacked" />
            </Col>
            <Col sm={6} lg={2}>
              <IndicatorCard title={t("Dispatches")} value={result.summary.dispatchCount} icon="fa-truck" />
            </Col>
            <Col sm={6} lg={2}>
              <IndicatorCard title={t("Products")} value={result.summary.distinctProducts} icon="fa-box" />
            </Col>
            <Col sm={6} lg={2}>
              <IndicatorCard title={t("Clients")} value={result.summary.distinctClients} icon="fa-users" />
            </Col>
            <Col sm={6} lg={2}>
              <IndicatorCard title={t("Warehouses")} value={result.summary.distinctWarehouses} icon="fa-warehouse" />
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={6}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white fw-semibold">
                  <i className="fas fa-ranking-star me-2 text-primary" />
                  {t("Top Products")}
                </Card.Header>
                <Card.Body className="p-0" style={{ maxHeight: 360, overflowY: "auto" }}>
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">{t("Description")}</th>
                        <th>{t("Brand")}</th>
                        <th className="text-end">{t("Total Qty")}</th>
                        <th className="text-end pe-3">{t("Dispatches")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {result.topProducts.length === 0 && (
                        <tr><td colSpan={4} className="text-center text-muted py-4">{t("No data available")}</td></tr>
                      )}
                      {result.topProducts.map((p, i) => (
                        <tr
                          key={i}
                          role="button"
                          className={selectedItemCode === p.itemCode ? "table-active" : ""}
                          onClick={() => filterByProduct(p.itemCode)}
                          title={t("Click to see recent dispatches of this product")}
                        >
                          <td className="ps-3">
                            <div className="fw-semibold">{p.description}</div>
                            <small className="text-muted">{p.itemCode}</small>
                          </td>
                          <td>{p.brand}</td>
                          <td className="text-end">{p.totalQuantity.toLocaleString()}</td>
                          <td className="text-end pe-3"><Badge bg="secondary">{p.dispatchCount}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white fw-semibold">
                  <i className="fas fa-warehouse me-2 text-primary" />
                  {t("By Warehouse")}
                </Card.Header>
                <Card.Body className="p-0" style={{ maxHeight: 360, overflowY: "auto" }}>
                  <Table hover responsive className="mb-0">
                    <tbody>
                      {result.byWarehouse.map((w, i) => (
                        <tr key={i}>
                          <td className="ps-3">{w.warehouse}</td>
                          <td className="text-end pe-3">{w.totalQuantity.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={3}>
              <Card className="shadow-sm border-0 h-100">
                <Card.Header className="bg-white fw-semibold">
                  <i className="fas fa-chart-line me-2 text-primary" />
                  {t("Monthly Trend")}
                </Card.Header>
                <Card.Body className="p-0" style={{ maxHeight: 360, overflowY: "auto" }}>
                  <Table hover responsive className="mb-0">
                    <tbody>
                      {result.monthlyTrend.map((m, i) => (
                        <tr key={i}>
                          <td className="ps-3 text-capitalize">{monthLabel(m)}</td>
                          <td className="text-end">{m.totalQuantity.toLocaleString()}</td>
                          <td className="text-end pe-3"><Badge bg="secondary">{m.dispatchCount}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="g-3 mb-4">
            <Col lg={6}>
              <Card className="shadow-sm border-0">
                <Card.Header className="bg-white fw-semibold d-flex align-items-center justify-content-between">
                  <span>
                    <i className="fas fa-location-dot me-2 text-primary" />
                    {t("Top Destinations")}
                    {selectedItemCode && (
                      <Badge bg="info" className="ms-2">{selectedItemCode}</Badge>
                    )}
                  </span>
                  {isLoadingTopClients && <Spinner animation="border" size="sm" variant="primary" />}
                </Card.Header>
                <Card.Body className="p-0" style={{ maxHeight: 400, overflowY: "auto" }}>
                  <Table hover responsive className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-3">#</th>
                        <th>{t("Client")}</th>
                        <th className="text-end">{t("Total Qty")}</th>
                        <th className="text-end pe-3">{t("Dispatches")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {topClients.length === 0 && !isLoadingTopClients && (
                        <tr><td colSpan={4} className="text-center text-muted py-4">{t("No data available")}</td></tr>
                      )}
                      {topClients.map((c, i) => (
                        <tr key={i}>
                          <td className="ps-3 text-muted">{i + 1}</td>
                          <td>
                            <div className="fw-semibold">{c.clientName}</div>
                            <small className="text-muted">{c.clientCode}</small>
                          </td>
                          <td className="text-end">{c.totalQuantity.toLocaleString()}</td>
                          <td className="text-end pe-3"><Badge bg="secondary">{c.dispatchCount}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white fw-semibold d-flex align-items-center justify-content-between">
              <span>
                <i className="fas fa-clock-rotate-left me-2 text-primary" />
                {t("Recent Dispatches")}
                {selectedItemCode && (
                  <Badge bg="info" className="ms-2" role="button" onClick={() => filterByProduct(selectedItemCode)}>
                    {selectedItemCode} <i className="fas fa-xmark ms-1" />
                  </Badge>
                )}
              </span>
              {isLoadingRecent && <Spinner animation="border" size="sm" variant="primary" />}
            </Card.Header>
            <Card.Body className="p-0">
              <Table hover responsive className="mb-0">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">{t("Date")}</th>
                    <th>{t("Description")}</th>
                    <th className="text-end">{t("Quantity")}</th>
                    <th>{t("Unit")}</th>
                    <th>{t("Warehouse")}</th>
                    <th>{t("Client")}</th>
                    <th>{t("Seller")}</th>
                    <th>{t("Conduce")}</th>
                    <th className="pe-3">{t("Invoice")}</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.length === 0 && !isLoadingRecent && (
                    <tr><td colSpan={9} className="text-center text-muted py-4">{t("No data available")}</td></tr>
                  )}
                  {recent.map((r, i) => (
                    <tr key={i}>
                      <td className="ps-3 text-nowrap">{moment(r.date).format("DD/MM/YYYY")}</td>
                      <td>
                        <div>{r.description}</div>
                        <small className="text-muted">{r.itemCode}</small>
                      </td>
                      <td className="text-end fw-semibold">{r.quantity.toLocaleString()}</td>
                      <td>{r.unit}</td>
                      <td>{r.warehouse}</td>
                      <td>{r.clientName}</td>
                      <td>{r.sellerName}</td>
                      <td><small>{r.conduceNumber}</small></td>
                      <td className="pe-3">
                        {r.invoiceNumber ? (
                          <button
                            className="btn btn-link btn-sm p-0 text-decoration-none"
                            title={t("Ver documentos relacionados")}
                            onClick={() => setTraceOrder(r.invoiceNumber)}
                          >
                            {r.invoiceNumber}
                          </button>
                        ) : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
            {totalPages > 1 && (
              <Card.Footer className="bg-white">
                <Pagination
                  totalPages={totalPages}
                  currentPage={page}
                  totalRecords={totalRecords}
                  onPageChanged={(p: number) => loadRecent(searchedTerm, selectedItemCode, p, startDate, endDate, dependency, warehouse)}
                />
              </Card.Footer>
            )}
          </Card>
        </>
      )}

      <OrderTracePanel
        show={!!traceOrder}
        orderNumber={traceOrder}
        organizationId={orgId}
        onHide={() => setTraceOrder("")}
      />
    </Container>
  )
}
